import React, { useEffect, useState } from "react";
import { useTable, useGlobalFilter, useSortBy } from "react-table";
import { Icon } from "@iconify/react";
import axiosInstance from "../axiosConfig.js";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSortUp, FaSortDown, FaSort } from "react-icons/fa";
import ViewContactModal from "./modals/ContactUs/ViewContactModal.jsx";
import StatusHistoryModal from "./modals/ContactUs/StatusHistoryModal.jsx";
import UpdateContactStatusModal from "./modals/ContactUs/UpdateContactStatusModal.jsx";
import DeleteModal from "./modals/DeleteModal.jsx";

const GlobalFilter = ({ globalFilter, setGlobalFilter }) => (
  <input
    className="form-control w-100"
    value={globalFilter || ""}
    onChange={(e) => setGlobalFilter(e.target.value)}
    placeholder="Search Contact Us..."
  />
);

const ContactUsLayer = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewContact, setViewContact] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");

  const pageSize = 10;

  useEffect(() => {
    fetchContacts();
  }, [page, search]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/contact-us", {
        params: { page, limit: pageSize, search },
      });
      setContacts(res.data.data.contacts || []);
      setTotalPages(res.data.data.pagination?.totalPages || 1);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
      setLoading(false);
    }
  };

  const handleViewContact = (contact) => {
    setViewContact(contact);
    setShowViewModal(true);
  };

  const handleUpdateContactStatus = (contact) => {
    setSelectedContact(contact);
    setShowUpdateModal(true);
  };

  const handleViewHistory = (contact) => {
    setStatusHistory(contact.statusHistory || []);
    setShowHistoryModal(true);
  };

  const handleDeleteContact = (contact) => {
    setSelectedContact(contact);
    setShowDeleteModal(true);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "#",
        Cell: ({ row }) => (page - 1) * pageSize + row.index + 1,
      },
      {
        Header: "View",
        Cell: ({ row }) => (
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => handleViewContact(row.original)}
          >
            View
          </button>
        ),
      },

      { Header: "Name", accessor: "name" },
      { Header: "Email", accessor: "email" },
      { Header: "Phone", accessor: "phoneNumber" },
      {
        Header: "Message",
        accessor: "message",
        Cell: ({ value }) => (
          <span
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: "300px",
              whiteSpace: "normal",
              wordBreak: "break-word",
              textAlign: "center",
              margin: "0 auto",
            }}
          >
            {value || "-"}
          </span>
        ),
      },
      {
        Header: "Status History",
        Cell: ({ row }) => (
          <button
            className="btn btn-sm btn-info"
            onClick={() => handleViewHistory(row.original)}
          >
            View History
          </button>
        ),
      },
      {
        Header: "Status",
        accessor: (row) => row?.status,
        Cell: ({ row }) => {
          const status = row.original.status;

          let badgeClass = "";
          switch (status) {
            case "closed":
              badgeClass = "bg-success"; // green
              break;
            case "ongoing":
              badgeClass = "bg-warning text-light"; // yellow with white text
              break;
            case "pending":
              badgeClass = "bg-primary"; // blue
              break;
            default:
              badgeClass = "bg-secondary"; // gray for unknown status
              break;
          }

          return (
            <span className={`badge ${badgeClass}`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          );
        },
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-primary"
              onClick={() => handleUpdateContactStatus(row.original)}
            >
              <Icon icon="mdi:pencil" />
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={() => handleDeleteContact(row.original)}
            >
              <Icon icon="mdi:delete" />
            </button>
          </div>
        ),
      },
    ],
    [page]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    setGlobalFilter,
  } = useTable({ columns, data: contacts }, useGlobalFilter, useSortBy);

  useEffect(() => {
    setGlobalFilter(search);
  }, [search, setGlobalFilter]);

  return (
    <div className="card basic-data-table" style={{ minHeight: "65vh" }}>
      <ToastContainer />
      <div className="card-header d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
        <h5 className="card-title mb-0 flex-shrink-0 w-35 w-md-100 w-sm-100">
          Contact Us
        </h5>
        <div className="w-35 w-md-100 w-sm-100">
          <GlobalFilter globalFilter={search} setGlobalFilter={setSearch} />
        </div>
      </div>

      <div className="card-body p-0 d-flex flex-column">
        {loading ? (
          <div className="text-center p-4">Loading...</div>
        ) : contacts.length === 0 ? (
          <div className="text-center p-4">No contacts found</div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table bordered-table mb-0" {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr
                      {...headerGroup.getHeaderGroupProps()}
                      key={headerGroup.id}
                    >
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                          key={column.id}
                          style={{ textAlign: "center", whiteSpace: "nowrap" }}
                        >
                          {column.render("Header")}{" "}
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <FaSortDown />
                            ) : (
                              <FaSortUp />
                            )
                          ) : (
                            <FaSort style={{ opacity: 0.3 }} />
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} key={row.id}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            key={cell.column.id}
                            style={{
                              textAlign: "center",
                              verticalAlign: "middle",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Backend Pagination */}
            <div className="d-flex justify-content-end mt-auto px-3 pb-4">
              <ul className="pagination mb-0">
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  >
                    Prev
                  </button>
                </li>
                {[...Array(totalPages).keys()].map((p) => (
                  <li
                    key={p}
                    className={`page-item ${p + 1 === page ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setPage(p + 1)}
                    >
                      {p + 1}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    page === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setPage((prev) => Math.min(prev + 1, totalPages))
                    }
                  >
                    Next
                  </button>
                </li>
              </ul>
            </div>
          </>
        )}
      </div>

      <ViewContactModal
        show={showViewModal}
        handleClose={() => setShowViewModal(false)}
        contact={viewContact}
      />

      <StatusHistoryModal
        show={showHistoryModal}
        handleClose={() => setShowHistoryModal(false)}
        history={statusHistory}
      />

      <UpdateContactStatusModal
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        quotation={selectedContact}
        fetchContactUs={fetchContacts}
      />

      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        item={selectedContact}
        itemType="contact-us"
        fetchData={fetchContacts}
      />
    </div>
  );
};

export default ContactUsLayer;
